import { useEffect } from 'react'
import { Routes, Route, Navigate, Outlet, useLocation, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  supportedLanguages,
  defaultLanguage,
  type SupportedLanguage,
} from '@/i18n'
import { Layout } from '@/components/layout'
import { HomePage } from '@/pages/HomePage'
import { AboutPage } from '@/pages/AboutPage'
import { ProductsPage } from '@/pages/ProductsPage'
import { ProductDetailPage } from '@/pages/ProductDetailPage'
import { SectorsPage } from '@/pages/SectorsPage'
import { SectorDetailPage } from '@/pages/SectorDetailPage'
import { AcademyPage } from '@/pages/AcademyPage'

import { ContactPage } from '@/pages/ContactPage'
import { LoginPage } from '@/pages/LoginPage'
import { RegisterPage } from '@/pages/RegisterPage'

import { LegalPage } from '@/pages/LegalPage'
import { NotFoundPage } from '@/pages/NotFoundPage'

function ScrollToTop() {
  const { pathname } = useLocation()
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])
  return null
}

function LanguageWrapper() {
  const location = useLocation()
  const { i18n } = useTranslation()

  // Extract lang from the first path segment (e.g., /it/chi-siamo → "it")
  const lang = location.pathname.split('/')[1] as SupportedLanguage | undefined

  useEffect(() => {
    if (lang && supportedLanguages.includes(lang)) {
      if (i18n.language !== lang) {
        i18n.changeLanguage(lang)
      }
    }
  }, [lang, i18n])

  if (!lang || !supportedLanguages.includes(lang)) {
    return <Navigate to={`/${defaultLanguage}`} replace />
  }

  return <Outlet />
}

/** Redirect old /prodotti/:categorySlug/:productSlug → /prodotti/:productSlug */
function LegacyProductRedirect({ langPrefix, productsPath }: { langPrefix: string; productsPath: string }) {
  const { productSlug } = useParams<{ productSlug: string }>()
  return <Navigate to={`/${langPrefix}/${productsPath}/${productSlug}`} replace />
}

/** Italian routes */
function ItRoutes() {
  return (
    <Route element={<Layout />}>
      <Route index element={<HomePage />} />
      <Route path="chi-siamo" element={<AboutPage />} />
      <Route path="chi-siamo/sostenibilita" element={<Navigate to="/it/chi-siamo" replace />} />
      <Route path="chi-siamo/certificazioni" element={<Navigate to="/it/chi-siamo" replace />} />
      <Route path="prodotti" element={<ProductsPage />} />
      <Route path="prodotti/sicurezza-urbana" element={<Navigate to="/it/prodotti?filter=urban-security" replace />} />
      <Route path="prodotti/controllo-accessi" element={<Navigate to="/it/prodotti?filter=access-control" replace />} />
      <Route path="prodotti/progetti-speciali" element={<Navigate to="/it/prodotti?filter=special-projects" replace />} />
      <Route path="prodotti/:categorySlug/:productSlug" element={<LegacyProductRedirect langPrefix="it" productsPath="prodotti" />} />
      <Route path="prodotti/:productSlug" element={<ProductDetailPage />} />
      <Route path="settori" element={<SectorsPage />} />
      <Route path="settori/:sectorSlug" element={<SectorDetailPage />} />
      <Route path="academy" element={<AcademyPage />} />

      <Route path="contatti" element={<ContactPage />} />
      <Route path="accedi" element={<LoginPage />} />
      <Route path="registrati" element={<RegisterPage />} />
      <Route path="privacy-policy" element={<LegalPage titleKey="legal.privacyTitle" contentKey="legal.privacyContent" />} />
      <Route path="cookie-policy" element={<LegalPage titleKey="legal.cookieTitle" contentKey="legal.cookieContent" />} />
      <Route path="dichiarazione-di-accessibilita" element={<LegalPage titleKey="legal.accessibilityTitle" contentKey="legal.accessibilityContent" />} />
      <Route path="vulnerability-disclosure-policy" element={<LegalPage titleKey="legal.vdpTitle" contentKey="legal.vdpContent" />} />
      <Route path="*" element={<NotFoundPage />} />
    </Route>
  )
}

/** English routes */
function EnRoutes() {
  return (
    <Route element={<Layout />}>
      <Route index element={<HomePage />} />
      <Route path="about-us" element={<AboutPage />} />
      <Route path="about-us/sustainability" element={<Navigate to="/en/about-us" replace />} />
      <Route path="about-us/certifications" element={<Navigate to="/en/about-us" replace />} />
      <Route path="products" element={<ProductsPage />} />
      <Route path="products/urban-security" element={<Navigate to="/en/products?filter=urban-security" replace />} />
      <Route path="products/access-control" element={<Navigate to="/en/products?filter=access-control" replace />} />
      <Route path="products/special-projects" element={<Navigate to="/en/products?filter=special-projects" replace />} />
      <Route path="products/:categorySlug/:productSlug" element={<LegacyProductRedirect langPrefix="en" productsPath="products" />} />
      <Route path="products/:productSlug" element={<ProductDetailPage />} />
      <Route path="sectors" element={<SectorsPage />} />
      <Route path="sectors/:sectorSlug" element={<SectorDetailPage />} />
      <Route path="academy" element={<AcademyPage />} />

      <Route path="contacts" element={<ContactPage />} />
      <Route path="login" element={<LoginPage />} />
      <Route path="register" element={<RegisterPage />} />
      <Route path="privacy-policy" element={<LegalPage titleKey="legal.privacyTitle" contentKey="legal.privacyContent" />} />
      <Route path="cookie-policy" element={<LegalPage titleKey="legal.cookieTitle" contentKey="legal.cookieContent" />} />
      <Route path="accessibility-statement" element={<LegalPage titleKey="legal.accessibilityTitle" contentKey="legal.accessibilityContent" />} />
      <Route path="vulnerability-disclosure-policy" element={<LegalPage titleKey="legal.vdpTitle" contentKey="legal.vdpContent" />} />
      <Route path="*" element={<NotFoundPage />} />
    </Route>
  )
}

export function App() {
  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* Redirect root to default language */}
        <Route path="/" element={<Navigate to={`/${defaultLanguage}`} replace />} />

        {/* Italian routes */}
        <Route path="/it/*" element={<LanguageWrapper />}>
          {ItRoutes()}
        </Route>

        {/* English routes */}
        <Route path="/en/*" element={<LanguageWrapper />}>
          {EnRoutes()}
        </Route>

        {/* Catch-all: redirect to default language */}
        <Route path="*" element={<Navigate to={`/${defaultLanguage}`} replace />} />
      </Routes>
    </>
  )
}
